import React, { useState, useCallback } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

interface LomavisDualSlider1Props {
    /** The default values for the slider */
    defaultValue?: [number, number];
    /** The current values for the slider (controlled) */
    value?: [number, number];
    /** Callback function when slider values change */
    onChange?: (value: [number, number]) => void;
    /** Minimum value */
    min?: number;
    /** Maximum value */
    max?: number;
    /** Step size for slider movement */
    step?: number;
    /** Label for the engagement rate axis */
    label?: string;
    /** Tooltip text for the left input field */
    tooltipTextLeft?: string;
    /** Tooltip text for the right input field */
    tooltipTextRight?: string;
}

/**
* Atoms: LomavisDualSlider1
*/
export const LomavisDualSlider1: React.FC<LomavisDualSlider1Props> = ({ 
    defaultValue = [1.3, 2.3],
    value,
    onChange,
    min = 0,
    max = 10,
    step = 0.1,
    label = "",
    tooltipTextLeft = "",
    tooltipTextRight = ""
}) => {
    const [internalValue, setInternalValue] = useState<[number, number]>(defaultValue);
    const [inputValues, setInputValues] = useState<[string, string]>([
        defaultValue[0].toString(),
        defaultValue[1].toString()
    ]);
    const currentValue = value || internalValue;

    // Update input values when currentValue changes externally
    React.useEffect(() => {
        setInputValues([currentValue[0].toString(), currentValue[1].toString()]);
    }, [currentValue[0], currentValue[1]]);

    const handleValueChange = useCallback((newValue: [number, number]) => {
        if (!value) {
            setInternalValue(newValue);
        }
        onChange?.(newValue);
    }, [value, onChange]);

    const handleLeftInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Ensure only one decimal point and only numbers
        let inputValue = e.target.value;
        
        // Replace multiple dots with a single dot and remove non-numeric characters
        inputValue = inputValue.replace(/\.+/g, '.').replace(/[^0-9.]/g, '');
        
        // Ensure only one decimal point total
        const parts = inputValue.split('.');
        if (parts.length > 2) {
            inputValue = parts[0] + '.' + parts.slice(1).join('');
        }
        
        setInputValues([inputValue, inputValues[1]]);
        
        if (inputValue && !isNaN(parseFloat(inputValue))) {
            const numericValue = parseFloat(inputValue);
            // Only constrain by min value, not by the other slider's value
            const constrainedValue = Math.max(min ?? 0, numericValue);
            handleValueChange([constrainedValue, currentValue[1]]);
        }
    };

    const handleRightInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Ensure only one decimal point and only numbers
        let inputValue = e.target.value;
        
        // Replace multiple dots with a single dot and remove non-numeric characters
        inputValue = inputValue.replace(/\.+/g, '.').replace(/[^0-9.]/g, '');
        
        // Ensure only one decimal point total
        const parts = inputValue.split('.');
        if (parts.length > 2) {
            inputValue = parts[0] + '.' + parts.slice(1).join('');
        }
        
        setInputValues([inputValues[0], inputValue]);
        
        if (inputValue && !isNaN(parseFloat(inputValue))) {
            const numericValue = parseFloat(inputValue);
            // Only constrain by max value, not by the other slider's value
            const constrainedValue = Math.min(max ?? 10, numericValue);
            handleValueChange([currentValue[0], constrainedValue]);
        }
    };

    // Calculate positions as percentages (used for reference)
    // const leftPosition = currentValue[0];
    // const rightPosition = currentValue[1];

    // Get emoji based on performance zones
    const getZoneEmoji = (position: 'left' | 'between' | 'right') => {
        switch (position) {
            case 'left': return '😐';
            case 'between': return '🎉';
            case 'right': return '🚀';
            default: return '😐';
        }
    };

    return (
        <div className="w-100">
            {/* Row with 5 cells: emoji | input | emoji | input | emoji */}
            <div className="row mb-4" style={{ minHeight: '38px' }}>
                {/* Cell 1: Left zone emoji */}
                <div className="col text-center d-flex align-items-center justify-content-center">
                    <span className="fs-5" style={{ lineHeight: 1 }}>{getZoneEmoji('left')}</span>
                </div>

                {/* Cell 2: First input with tooltip icon */}
                <div className="col text-center d-flex align-items-center justify-content-center">
                    <div className="d-inline-flex align-items-center gap-1">
                        <div className="input-group input-group-sm">
                            <input 
                                type="text"
                                className="form-control form-control-sm text-center border-secondary"
                                value={inputValues[0]}
                                onChange={handleLeftInputChange}
                                onKeyDown={(e) => {
                                    // Allow navigation keys, special keys, and decimal point
                                    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'NumpadDecimal', '.', 
                                                        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                                    
                                    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                    if ((e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) || 
                                        allowedKeys.includes(e.key)) {
                                        return;
                                    }
                                    
                                    // Allow numbers (0-9)
                                    if (/^\d$/.test(e.key)) {
                                        return;
                                    }
                                    
                                    // Block all other keys
                                    e.preventDefault();
                                }}
                            />
                            <div className="input-group-append">
                                <span className="input-group-text">%</span>
                            </div>
                        </div>
                        {tooltipTextLeft && (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip>
                                        <span className="fw-normal d-block small">
                                            {tooltipTextLeft}
                                        </span>
                                    </Tooltip>
                                }
                            >
                                <i className="fas fa-info-circle text-muted ms-1" style={{ cursor: 'pointer' }}></i>
                            </OverlayTrigger>
                        )}
                    </div>
                </div>

                {/* Cell 3: Between zone emoji */}
                <div className="col text-center d-flex align-items-center justify-content-center">
                    <span className="fs-5" style={{ lineHeight: 1 }}>{getZoneEmoji('between')}</span>
                </div>

                {/* Cell 4: Second input with tooltip icon */}
                <div className="col text-center d-flex align-items-center justify-content-center">
                    <div className="d-inline-flex align-items-center gap-1">
                        <div className="input-group input-group-sm" >
                            <input 
                                type="text"
                                className="form-control form-control-sm text-center border-secondary"
                                value={inputValues[1]}
                                onChange={handleRightInputChange}
                                onKeyDown={(e) => {
                                    // Allow navigation keys, special keys, and decimal point
                                    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'NumpadDecimal', '.', 
                                                        'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                                    
                                    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
                                    if ((e.ctrlKey && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) || 
                                        allowedKeys.includes(e.key)) {
                                        return;
                                    }
                                    
                                    // Allow numbers (0-9)
                                    if (/^\d$/.test(e.key)) {
                                        return;
                                    }
                                    
                                    // Block all other keys
                                    e.preventDefault();
                                }}
                            />
                            <div className="input-group-append">
                                <span className="input-group-text">%</span>
                            </div>
                        </div>
                        {tooltipTextRight && (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip>
                                        <span className="fw-normal d-block small">
                                            {tooltipTextRight}
                                        </span>
                                    </Tooltip>
                                }
                            >
                                <i className="fas fa-info-circle text-muted ms-1" style={{ cursor: 'pointer' }}></i>
                            </OverlayTrigger>
                        )}
                    </div>
                </div>

                {/* Cell 5: Right zone emoji */}
                <div className="col text-center d-flex align-items-center justify-content-center">
                    <span className="fs-5" style={{ lineHeight: 1 }}>{getZoneEmoji('right')}</span>
                </div>
            </div>

            {/* Horizontal Line with Arrow */}
            <div className="d-flex align-items-center mb-3">
                <span className="text-muted small me-3">0%</span>
                <div className="flex-grow-1 position-relative" style={{
                    height: '2px',
                    backgroundColor: 'var(--bs-secondary)'
                }}>
                    <div 
                        className="position-absolute"
                        style={{
                            content: '',
                            right: '0',
                            top: '50%',
                            transform: 'translateY(-50%) rotate(45deg)',
                            width: '10px',
                            height: '10px',
                            borderTop: '2px solid var(--bs-secondary)',
                            borderRight: '2px solid var(--bs-secondary)'
                        }}
                    ></div>
                </div>
            </div>

            {/* Engagement Rate label */}
            {label && (
                <div className="text-center">
                    <span className="text-muted">{label}</span>
                </div>
            )}
        </div>
    );
};
